import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchItems } from '../api/items';

/**
 * Custom hook for item search functionality with manual search triggers, filtering, and pagination
 *
 * @param {Object} options - Search options
 * @param {string} [options.filter] - Filter type: "review" or "status:{value}"
 * @returns {Object} Search state and handlers with page-based pagination
 */
export function useItemSearch(options = {}) {
  const { filter } = options;

  const [searchQuery, setSearchQuery] = useState(''); // Actual query used for API calls
  const [searchTerm, setSearchTerm] = useState(''); // Input field value
  const [currentPage, setCurrentPage] = useState(0);

  // Query for search results with manual search query
  const {
    data: searchData,
    isLoading: isSearching,
    error,
    isPlaceholderData,
  } = useQuery({
    queryKey: ['items', 'search', searchQuery, currentPage, filter],
    queryFn: async () => {
      if (!searchQuery.trim()) {
        return null;
      }
      return searchItems(searchQuery.trim(), {
        page: currentPage,
        limit: 20,
        filter,
      });
    },
    enabled: !!searchQuery.trim(),
    placeholderData: (previousData) => previousData, // Keep previous results while loading new ones
    staleTime: 60000, // Consider data fresh for 1 minute
    gcTime: 300000, // Keep in cache for 5 minutes
  });

  // Memoized search results data
  const searchResults = useMemo(() => {
    if (!searchData || !searchData.hits) {
      return {
        items: [],
        totalHits: 0,
        totalPages: 0,
        currentPage: 0,
        hasNextPage: false,
        hasPrevPage: false,
        processingTime: 0,
      };
    }

    return {
      items: searchData.hits,
      totalHits: searchData.nbHits,
      totalPages: searchData.nbPages,
      currentPage: searchData.page,
      hasNextPage: searchData.page < searchData.nbPages - 1,
      hasPrevPage: searchData.page > 0,
      processingTime: searchData.processingTimeMS,
    };
  }, [searchData]);

  // Memoized handlers
  // handleSearch accepts an optional query parameter for direct search triggering
  const handleSearch = useCallback((query) => {
    const queryToUse = query !== undefined ? query : searchTerm;
    const trimmedQuery = queryToUse.trim();
    if (trimmedQuery && trimmedQuery !== searchQuery.trim()) {
      setSearchQuery(trimmedQuery);
      setCurrentPage(0); // Reset to first page when search query changes
    }
  }, [searchTerm, searchQuery]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setSearchQuery('');
    setCurrentPage(0);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 0 && newPage < searchResults.totalPages) {
      setCurrentPage(newPage);
    }
  }, [searchResults.totalPages]);

  const handleNextPage = useCallback(() => {
    if (searchResults.hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  }, [searchResults.hasNextPage]);

  const handlePrevPage = useCallback(() => {
    if (searchResults.hasPrevPage) {
      setCurrentPage(prev => Math.max(0, prev - 1));
    }
  }, [searchResults.hasPrevPage]);

  // Determine if we should show results
  const hasQuery = searchQuery.trim().length > 0;
  const shouldShowResults = hasQuery && searchData;
  const shouldShowEmptyState = hasQuery && !isSearching && searchResults.items.length === 0;

  // Memoized return object to prevent unnecessary re-renders
  return useMemo(() => ({
    searchQuery,
    searchTerm,
    setSearchTerm,
    searchResults,
    isSearching: isSearching && !isPlaceholderData,
    hasQuery,
    shouldShowResults,
    shouldShowEmptyState,
    handleSearch,
    handleClearSearch,
    handlePageChange,
    handleNextPage,
    handlePrevPage,
    error
  }), [
    searchQuery,
    searchTerm,
    setSearchTerm,
    searchResults,
    isSearching,
    isPlaceholderData,
    hasQuery,
    shouldShowResults,
    shouldShowEmptyState,
    handleSearch,
    handleClearSearch,
    handlePageChange,
    handleNextPage,
    handlePrevPage,
    error
  ]);
}

export default useItemSearch;
